import React from 'react'
import { createGlobalStyle } from 'styled-components'
import { ToastContainer } from 'react-toastify'
import { transparentize } from 'polished'
import { font, bp } from 'lib/core/resources/helpers/styled'

const animations = `
  @keyframes Toastify__trackProgress {
    0% {
      transform: scaleX(1);
    }
    100% {
      transform: scaleX(0);
    }
  }
  
  @keyframes Toastify__bounceInRight {
    from,
    60%,
    75%,
    90%,
    to {
      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
    }
    from {
      opacity: 0;
      transform: translate3d(3000px, 0, 0);
    }
    60% {
      opacity: 1;
      transform: translate3d(-25px, 0, 0);
    }
    75% {
      transform: translate3d(10px, 0, 0);
    }
    90% {
      transform: translate3d(-5px, 0, 0);
    }
    to {
      transform: none;
    }
  }
  
  @keyframes Toastify__bounceOutRight {
    20% {
      opacity: 1;
      transform: translate3d(-20px, 0, 0);
    }
    to {
      opacity: 0;
      transform: translate3d(2000px, 0, 0);
    }
  }
`

const ToastNotificationStyles = createGlobalStyle`
  ${animations}

  .Toastify__toast-container {
    user-select: none;
    padding: 0 !important;
    transform: translate(-20px, 140px) !important;
    margin-left: 0 !important;
    z-index: 9999;
    position: fixed;
    width: 320px;
    box-sizing: border-box;
    color: ${props => props.theme.palette.general.light};
    ${bp('lg')`
      transform: translate(-20px, 85px) !important;
    `}

    ${bp('sm')`
      width: 100vw !important;
      padding: 0;
      transform: none !important;
      left: 0 !important;
      margin: 0 !important;
    `}
  }

  .Toastify__toast-container--top-left,
  .Toastify__toast-container--top-center,
  .Toastify__toast-container--top-right {
    ${bp('sm')`
      top: 0
    `}
  }

  .Toastify__toast-container--bottom-left,
  .Toastify__toast-container--bottom-center,
  .Toastify__toast-container--bottom-right {
    ${bp('sm')`
      bottom: 0;
    `}
  }

  .Toastify__toast-container--rtl {
    ${bp('sm')`
      right: 0;
      left: initial;
    `}
  }

  .Toastify__toast {
    cursor: default !important;
    text-align: center;
    min-height: unset !important;
    padding: 0 !important;
    box-sizing: border-box;
    border-radius: 0 !important;
    position: relative;
    margin-bottom: 1rem;
    box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.1), 0 2px 15px 0 rgba(0, 0, 0, 0.05);
    display: flex;
    justify-content: space-between;
    max-height: 800px;
    overflow: hidden;
    font-family: sans-serif;
    direction: ltr;
    ${bp('sm')`
      margin-bottom: 0;
    `}
  }

  .Toastify__toast-container--top-right {
    top: 0 !important;
    right: 0 !important;
  }

  .Toastify__toast-container--top-left {
    top: 1em;
    left: 1em;
  }

  .Toastify__toast-container--top-center {
    top: 1em;
    left: 50%;
    margin-left: -160px;
  }

  .Toastify__toast-container--bottom-right {
    bottom: 1em;
    right: 1em;
  }

  .Toastify__toast-container--bottom-center {
    bottom: 1em;
    left: 50%;
    margin-left: -160px;
  }

  .Toastify__toast-container--bottom-left {
    bottom: 1em;
    left: 1em;
  }

  .Toastify__toast-body {
    font: ${font('default', 'medium', '14px/1.5')};
    margin: auto 0;
    flex: 1;
  }

  .Toastify__toast--rtl {
    direction: rtl;
  }

  .Toastify__toast--default {
    background: #fff;
    color: #aaa;
  }

  .Toastify__toast--info {
    background: #3498db;
  }

  .Toastify__toast--success {
    background: #07bc0c;
  }

  .Toastify__toast--warning {
    background: #f1c40f;
  }

  .Toastify__toast--error {
    background: #e74c3c;
  }

  .Toastify__close-button {
    color: #fff;
    font-weight: bold;
    font-size: 14px;
    background: transparent;
    outline: none;
    border: none;
    padding: 0;
    cursor: pointer;
    opacity: 0.7;
    transition: 0.3s ease;
    align-self: flex-start;
  }

  .Toastify__close-button--default {
    color: #000;
    opacity: 0.3;
  }

  .Toastify__close-button:hover,
  .Toastify__close-button:focus {
    opacity: 1;
  }

  .Toastify__progress-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 5px;
    z-index: 9999;
    opacity: 0.7;
    background-color: ${props => transparentize(0.3, props.theme.palette.general.light)};
    transform-origin: left;
  }

  .Toastify__progress-bar--animated {
    animation: Toastify__trackProgress linear 1 forwards;
  }

  .Toastify__progress-bar--controlled {
    transition: transform 0.2s;
  }

  .Toastify__progress-bar--rtl {
    right: 0;
    left: initial;
    transform-origin: right;
  }

  .Toastify__progress-bar--default {
    background: linear-gradient(to right, #4cd964, #5ac8fa, #007aff, #34aadc, #5856d6, #ff2d55);
  }

  .Toastify__bounce-enter--top-right,
  .Toastify__bounce-enter--bottom-right {
    animation-name: Toastify__bounceInRight;
  }

  .Toastify__bounce-exit--top-right,
  .Toastify__bounce-exit--bottom-right {
    animation-name: Toastify__bounceOutRight;
  }
`

export const ToastNotification = () => {
  return (
    <>
      <ToastNotificationStyles />
      <ToastContainer
        newestOnTop
        hideProgressBar
        closeOnClick
        draggable={false}
        closeButton={false}
        autoClose={5000}
        position='top-right'
      />
    </>
  )
}
